import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, getDoc, query, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';

export default function AdminDashboard() {
  const [properties, setProperties] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();

  // Form States for Adding/Editing Property (Updated with new dynamic fields)
  const [title, setTitle] = useState('');
  const [type, setType] = useState('1 BHK Flat');
  const [location, setLocation] = useState('');
  const [price, setPrice] = useState('');
  const [builder, setBuilder] = useState('');
  const [furnishing, setFurnishing] = useState('Semi-Furnished');
  const [area, setArea] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  
  // Edit Mode States
  const [isEditing, setIsEditing] = useState(false);
  const [currentEditId, setCurrentEditId] = useState(null);

  // Professional Toast Notification State
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3500);
  };

  // Helper Function to Format Price in Indian Currency (e.g., 75,00,000)
  const formatIndianPrice = (amount) => {
    if (!amount && amount !== 0) return '';
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Fetch Data from Firestore (Properties, Customers, Inquiries)
  const fetchAdminData = async () => {
    try {
      const propertySnapshot = await getDocs(collection(db, "properties"));
      const propertyList = propertySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProperties(propertyList);

      const usersSnapshot = await getDocs(collection(db, "users"));
      const userList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCustomers(userList);

      try {
        const inquiryQuery = query(collection(db, "contacts"), orderBy("createdAt", "desc"));
        const inquirySnapshot = await getDocs(inquiryQuery);
        const inquiryList = inquirySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setInquiries(inquiryList);
      } catch (err) {
        const inquirySnapshot = await getDocs(collection(db, "contacts"));
        const inquiryList = inquirySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setInquiries(inquiryList);
      }
    } catch (error) {
      console.error("Error fetching admin data:", error);
      showToast("Error loading data from database.", "error");
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        showToast("Please login first!", "error");
        setTimeout(() => navigate('/auth'), 1500);
        return;
      }

      try {
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists() && userDocSnap.data().isAdmin === true) {
          await fetchAdminData();
        } else {
          showToast("Access Denied! You are not an admin.", "error");
          setTimeout(() => navigate('/'), 1500);
        }
      } catch (error) {
        console.error("Error checking admin status:", error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Handle Image Selection, Convert to WebP & Compress
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showToast("Image size should be less than 5MB.", "error");
      e.target.value = null;
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        const MAX_WIDTH = 800;
        const MAX_HEIGHT = 800;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height = Math.round((height * MAX_WIDTH) / width);
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width = Math.round((width * MAX_HEIGHT) / height);
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        const webpBase64 = canvas.toDataURL('image/webp', 0.8);
        setImagePreview(webpBase64);
      };

      img.onerror = () => {
        showToast("Invalid image file selected.", "error");
      };
    };

    reader.onerror = () => {
      showToast("Failed to read file.", "error");
    };

    reader.readAsDataURL(file);
  };

  const handleSubmitProperty = async (e) => {
    e.preventDefault();
    if (!imagePreview) {
      showToast("Please upload an image for the property.", "error");
      return;
    }

    try {
      const propertyData = {
        title,
        type,
        location,
        price: Number(price),
        builder,
        furnishing,
        area,
        image: imagePreview,
      };

      if (isEditing) {
        const propertyRef = doc(db, "properties", currentEditId);
        const updatedData = { ...propertyData, updatedAt: new Date() };

        await updateDoc(propertyRef, updatedData);
        setProperties(properties.map(p => p.id === currentEditId ? { id: currentEditId, ...updatedData } : p));
        showToast("Property updated successfully!", "success");
        
        setIsEditing(false);
        setCurrentEditId(null);
      } else {
        const newProperty = { ...propertyData, createdAt: new Date() };
        const docRef = await addDoc(collection(db, "properties"), newProperty);
        setProperties([...properties, { id: docRef.id, ...newProperty }]);
        showToast("Property added successfully!", "success");
      }
      
      setTitle('');
      setType('1 BHK Flat');
      setLocation('');
      setPrice('');
      setBuilder('');
      setFurnishing('Semi-Furnished');
      setArea('');
      setImagePreview('');
    } catch (error) {
      console.error("Error saving property:", error);
      showToast("Failed to save property.", "error");
    }
  };

  const handleEditClick = (property) => {
    setIsEditing(true);
    setCurrentEditId(property.id);
    setTitle(property.title);
    setType(property.type);
    setLocation(property.location);
    setPrice(property.price);
    setBuilder(property.builder || '');
    setFurnishing(property.furnishing || 'Semi-Furnished');
    setArea(property.area || '');
    setImagePreview(property.image);
    setActiveTab('properties');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setCurrentEditId(null);
    setTitle('');
    setType('1 BHK Flat');
    setLocation('');
    setPrice('');
    setBuilder('');
    setFurnishing('Semi-Furnished');
    setArea('');
    setImagePreview('');
  };

  const handleDeleteProperty = async (id) => {
    if (window.confirm("Are you sure you want to delete this property?")) {
      try {
        await deleteDoc(doc(db, "properties", id));
        setProperties(properties.filter(p => p.id !== id));
        showToast("Property deleted successfully.", "success");
      } catch (error) {
        console.error("Error deleting property:", error);
        showToast("Failed to delete property.", "error");
      }
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/auth');
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center font-sans">
        <p className="text-slate-400 text-sm animate-pulse">Verifying Admin Access & Loading Data...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6 md:p-10 font-sans relative">
      
      {toast.show && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-4 rounded-2xl shadow-2xl border text-sm font-medium transition-all transform translate-y-0 ${
          toast.type === 'success' 
            ? 'bg-slate-900 border-emerald-500/50 text-emerald-400 shadow-emerald-950/50' 
            : 'bg-slate-900 border-rose-500/50 text-rose-400 shadow-rose-950/50'
        }`}>
          <span className={`w-2.5 h-2.5 rounded-full ${toast.type === 'success' ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500 animate-pulse'}`}></span>
          {toast.message}
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-slate-900 border border-slate-800 p-6 rounded-3xl mb-8 gap-4 shadow-xl">
          <div>
            <span className="bg-rose-500/20 text-rose-400 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider">
              Admin Control Center
            </span>
            <h1 className="text-3xl font-extrabold mt-2 text-white">PrimiumFlats Dashboard</h1>
            <p className="text-slate-400 text-sm">Manage properties, track customer registrations, and view inquiries.</p>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/')}
              className="bg-slate-800 hover:bg-slate-700 px-4 py-2.5 rounded-xl text-xs font-medium transition border border-slate-700 text-slate-300"
            >
              Go to Website (Home)
            </button>
            <button 
              onClick={handleLogout}
              className="bg-rose-600 hover:bg-rose-700 px-4 py-2.5 rounded-xl text-xs font-semibold transition shadow-lg text-white"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Quick Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl shadow-xl">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Registered Customers</p>
            <h3 className="text-3xl font-extrabold text-indigo-400 mt-2">{customers.length}</h3>
          </div>
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl shadow-xl">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Properties Listed</p>
            <h3 className="text-3xl font-extrabold text-emerald-400 mt-2">{properties.length}</h3>
          </div>
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl shadow-xl">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Customer Inquiries</p>
            <h3 className="text-3xl font-extrabold text-amber-400 mt-2">{inquiries.length}</h3>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-800 gap-6 text-sm font-semibold mb-8 overflow-x-auto">
          <button 
            onClick={() => setActiveTab('overview')} 
            className={`pb-3 border-b-2 transition whitespace-nowrap ${activeTab === 'overview' ? 'border-rose-500 text-rose-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
          >
            Dashboard Overview
          </button>
          <button 
            onClick={() => setActiveTab('customers')} 
            className={`pb-3 border-b-2 transition whitespace-nowrap ${activeTab === 'customers' ? 'border-rose-500 text-rose-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
          >
            Customer Details ({customers.length})
          </button>
          <button 
            onClick={() => setActiveTab('inquiries')} 
            className={`pb-3 border-b-2 transition whitespace-nowrap ${activeTab === 'inquiries' ? 'border-rose-500 text-rose-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
          >
            Inquiries ({inquiries.length})
          </button>
          <button 
            onClick={() => setActiveTab('properties')} 
            className={`pb-3 border-b-2 transition whitespace-nowrap ${activeTab === 'properties' ? 'border-rose-500 text-rose-400' : 'border-transparent text-slate-400 hover:text-slate-200'}`}
          >
            Manage Properties ({properties.length})
          </button>
        </div>

        {/* TAB 1: OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="bg-slate-900 border border-slate-800 p-8 rounded-3xl shadow-xl space-y-4">
            <h2 className="text-xl font-bold text-white">Welcome back, Admin!</h2>
            <p className="text-sm text-slate-400 leading-relaxed">
              You are logged into the secure admin control panel. Here you can check customers, phone numbers, review inquiries, and manage real estate listings seamlessly.
            </p>
          </div>
        )}

        {/* TAB 2: CUSTOMER DETAILS */}
        {activeTab === 'customers' && (
          <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
            <div className="p-6 border-b border-slate-800">
              <h2 className="text-lg font-bold text-white">Registered Customers List</h2>
              <p className="text-xs text-slate-400 mt-0.5">All users and phone numbers registered on PrimiumFlats</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-800/50 text-slate-400 uppercase tracking-wider border-b border-slate-800">
                    <th className="p-4">#</th>
                    <th className="p-4">Customer</th>
                    <th className="p-4">Phone Number</th>
                    <th className="p-4">User ID</th>
                    <th className="p-4">Role</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800 text-sm">
                  {customers.length > 0 ? (
                    customers.map((cust, idx) => {
                      const displayName = cust.name || cust.fullName || (cust.email ? cust.email.split('@')[0] : 'User');
                      return (
                        <tr key={cust.id} className="hover:bg-slate-800/30 transition">
                          <td className="p-4 text-slate-400 text-xs">{idx + 1}</td>
                          <td className="p-4 flex items-center gap-3">
                            {cust.profileImage ? (
                              <img 
                                src={cust.profileImage} 
                                alt="Customer" 
                                className="w-9 h-9 rounded-full object-cover border border-slate-700 shadow"
                              />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center font-bold text-xs text-white uppercase shadow">
                                {displayName.charAt(0)}
                              </div>
                            )}
                            <div>
                              <div className="font-medium text-white">{displayName}</div>
                              <div className="text-slate-400 text-xs">{cust.email || 'No email'}</div>
                            </div>
                          </td>
                          <td className="p-4 text-emerald-400 font-medium">{cust.phone || 'Not provided'}</td>
                          <td className="p-4 text-slate-400 font-mono text-xs">{cust.id}</td>
                          <td className="p-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${cust.isAdmin ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30' : 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'}`}>
                              {cust.isAdmin ? 'Admin' : 'Customer'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="p-8 text-center text-slate-500 text-sm">No customers registered yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 3: INQUIRIES */}
        {activeTab === 'inquiries' && (
          <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
            <div className="p-6 border-b border-slate-800">
              <h2 className="text-lg font-bold text-white">Customer Inquiries</h2>
              <p className="text-xs text-slate-400 mt-0.5">Messages, inquiry categories, and contact details sent by visitors</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-800/50 text-slate-400 uppercase tracking-wider border-b border-slate-800">
                    <th className="p-4">#</th>
                    <th className="p-4">Sender Name / Email</th>
                    <th className="p-4">Mobile Number</th>
                    <th className="p-4">Inquiry Type</th>
                    <th className="p-4">Message</th>
                    <th className="p-4">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800 text-sm">
                  {inquiries.length > 0 ? (
                    inquiries.map((inq, idx) => (
                      <tr key={inq.id} className="hover:bg-slate-800/30 transition">
                        <td className="p-4 text-slate-400 text-xs">{idx + 1}</td>
                        <td className="p-4 font-medium text-white">
                          <div>{inq.name || 'Anonymous'}</div>
                          <div className="text-slate-400 text-xs">{inq.email || 'No email'}</div>
                        </td>
                        <td className="p-4 text-emerald-400 font-medium">
                          {inq.mobile || inq.phone || 'Not provided'}
                        </td>
                        <td className="p-4">
                          <span className="bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 px-2.5 py-1 rounded-lg text-xs font-medium">
                            {inq.inquiryType || 'General Inquiry'}
                          </span>
                        </td>
                        <td className="p-4 text-slate-300 max-w-xs truncate">{inq.message || 'No message provided'}</td>
                        <td className="p-4 text-slate-400 text-xs">
                          {inq.createdAt?.seconds ? new Date(inq.createdAt.seconds * 1000).toLocaleDateString() : 'Recent'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="p-8 text-center text-slate-500 text-sm">
                        No inquiries received yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 4: MANAGE PROPERTIES */}
        {activeTab === 'properties' && (
          <div>
            <div className="bg-slate-900 border border-slate-800 p-8 rounded-3xl mb-12 shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-indigo-400">
                  {isEditing ? "Edit Property Listing" : "Add New Property Listing"}
                </h2>
                {isEditing && (
                  <button 
                    type="button" 
                    onClick={handleCancelEdit}
                    className="text-xs bg-slate-800 text-slate-300 hover:bg-slate-700 px-3 py-1.5 rounded-lg border border-slate-700 transition"
                  >
                    Cancel Edit
                  </button>
                )}
              </div>

              <form onSubmit={handleSubmitProperty} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Property Title</label>
                  <input 
                    type="text" 
                    placeholder="e.g. Hiranandani Estate Garden View" 
                    value={title} 
                    onChange={(e) => setTitle(e.target.value)} 
                    required
                    className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Property Type</label>
                  <select 
                    value={type} 
                    onChange={(e) => setType(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-indigo-500"
                  >
                    <option value="1 BHK Flat">1 BHK Flat</option>
                    <option value="2 BHK Flat">2 BHK Flat</option>
                    <option value="3 BHK Flat">3 BHK Flat</option>
                    <option value="Luxury Villa">Luxury Villa</option>
                    <option value="Penthouse">Penthouse</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Location / City</label>
                  <input 
                    type="text" 
                    placeholder="e.g. Powai, Mumbai" 
                    value={location} 
                    onChange={(e) => setLocation(e.target.value)} 
                    required
                    className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">
                    Price (₹) {price && <span className="text-indigo-400 font-normal lowercase">({formatIndianPrice(price)})</span>}
                  </label>
                  <input 
                    type="number" 
                    placeholder="e.g. 21000000" 
                    value={price} 
                    onChange={(e) => setPrice(e.target.value)} 
                    required
                    className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Builder / Owner</label>
                  <input 
                    type="text" 
                    placeholder="e.g. House of Hiranandani" 
                    value={builder} 
                    onChange={(e) => setBuilder(e.target.value)} 
                    required
                    className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Furnishing</label>
                  <select 
                    value={furnishing} 
                    onChange={(e) => setFurnishing(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-indigo-500"
                  >
                    <option value="Unfurnished">Unfurnished</option>
                    <option value="Semi-Furnished">Semi-Furnished</option>
                    <option value="Fully Furnished">Fully Furnished</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Area (sq.ft.)</label>
                  <input 
                    type="text" 
                    placeholder="e.g. 1,050 sq.ft." 
                    value={area} 
                    onChange={(e) => setArea(e.target.value)} 
                    required
                    className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">
                    Upload Image
                  </label>
                  <input 
                    type="file" 
                    accept="image/png, image/jpeg, image/jpg, image/webp"
                    onChange={handleImageChange} 
                    className="w-full bg-slate-800 border border-slate-700 text-slate-300 px-4 py-3 rounded-xl text-sm focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-600 file:text-white hover:file:bg-indigo-700 cursor-pointer"
                  />
                  {imagePreview && (
                    <div className="mt-4">
                      <p className="text-xs text-slate-400 mb-2">Preview:</p>
                      <img src={imagePreview} alt="Preview" className="w-32 h-32 object-cover rounded-xl border border-slate-700 shadow-md" />
                    </div>
                  )}
                </div>

                <div className="md:col-span-2">
                  <button 
                    type="submit"
                    className={`w-full font-semibold py-3.5 rounded-xl transition shadow-lg text-sm ${
                      isEditing 
                        ? 'bg-amber-600 hover:bg-amber-700 text-white' 
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    }`}
                  >
                    {isEditing ? "Update Property Listing" : "Publish Property"}
                  </button>
                </div>
              </form>
            </div>

            <h2 className="text-xl font-bold mb-6 text-slate-200">All Properties ({properties.length})</h2>

            {properties.length === 0 ? (
              <div className="bg-slate-900 border border-slate-800 p-12 text-center rounded-3xl text-slate-400">
                No properties found in database. Add your first property above!
              </div>
            ) : (
              <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-800/60 border-b border-slate-800 text-slate-400 text-xs uppercase tracking-wider">
                        <th className="p-4">Property</th>
                        <th className="p-4">Type / Builder</th>
                        <th className="p-4">Location / Area</th>
                        <th className="p-4">Furnishing</th>
                        <th className="p-4">Price</th>
                        <th className="p-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800 text-sm">
                      {properties.map(property => (
                        <tr key={property.id} className="hover:bg-slate-800/40 transition">
                          <td className="p-4 flex items-center gap-3">
                            <img 
                              src={property.image} 
                              alt="" 
                              className="w-12 h-12 rounded-xl object-cover border border-slate-700" 
                            />
                            <span className="font-semibold text-white">{property.title}</span>
                          </td>
                          <td className="p-4">
                            <div className="text-slate-300">{property.type}</div>
                            <div className="text-slate-500 text-xs">{property.builder || 'N/A'}</div>
                          </td>
                          <td className="p-4">
                            <div className="text-slate-300">{property.location}</div>
                            <div className="text-slate-500 text-xs">{property.area || 'N/A'}</div>
                          </td>
                          <td className="p-4 text-slate-300">{property.furnishing || 'N/A'}</td>
                          <td className="p-4 font-bold text-indigo-400">
                            ₹ {formatIndianPrice(property.price)}
                          </td>
                          <td className="p-4 text-right space-x-2">
                            <button 
                              onClick={() => handleEditClick(property)}
                              className="bg-amber-500/20 text-amber-400 hover:bg-amber-600 hover:text-white px-3 py-1.5 rounded-xl text-xs font-semibold transition"
                            >
                              Edit
                            </button>
                            <button 
                              onClick={() => handleDeleteProperty(property.id)}
                              className="bg-rose-500/20 text-rose-400 hover:bg-rose-600 hover:text-white px-3 py-1.5 rounded-xl text-xs font-semibold transition"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}